import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/assets/images/logo.png";
import { apiLogin, apiGoogleAuth } from "@/features/auth/api";
import { useAuthStore } from "@/features/auth/auth-store";
import { trackEvent } from "@/lib/analytics";
import { getGoogleIdentity } from "@/lib/google-identity";

// TODO: Replace with your actual Google Client ID from Google Cloud Console
const GOOGLE_CLIENT_ID = "GOOGLE_CLIENT_ID_PLACEHOLDER";

const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type LoginForm = z.infer<typeof loginSchema>;

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginForm) => {
    setError(null);
    try {
      const res = await apiLogin(data);
      setAuth({ token: res.token, user: res.user });
      trackEvent("login", { method: "email" });
      navigate("/home");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    }
  };

  const handleGoogleSignIn = () => {
    const google = getGoogleIdentity();
    if (!google) {
      setError("Google Sign-In not available");
      return;
    }
    google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async (response) => {
        setError(null);
        try {
          const res = await apiGoogleAuth(response.credential);
          setAuth({ token: res.token, user: res.user });
          trackEvent("login", { method: "google" });
          navigate("/home");
        } catch (err) {
          setError(err instanceof Error ? err.message : "Google sign-in failed");
        }
      },
    });
    google.accounts.id.prompt();
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-hero px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="text-center animate-fade-in-up">
          <img
            src={Logo}
            alt="SmartPoker"
            className="mx-auto w-20 h-20 rounded-2xl shadow-glow"
          />
          <h1 className="text-2xl font-bold text-white mt-4 tracking-tight">
            {t("login_title")}
          </h1>
          <p className="text-sm text-white/60 mt-1">
            {t("login_description")}
          </p>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="glass p-6 space-y-4 animate-fade-in-up"
          style={{ animationDelay: "100ms" }}
          aria-label="Login"
        >
          <div className="space-y-2">
            <Label htmlFor="email" className="text-white/80">
              {t("login_email_label")}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="m@example.com"
              autoComplete="email"
              className="bg-white/[0.06] border-white/[0.1] text-white placeholder:text-white/30 focus:border-secondary/50 focus:ring-secondary/20"
              {...register("email")}
            />
            {errors.email && (
              <p className="text-xs text-destructive" role="alert">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-white/80">
              {t("login_password_label")}
            </Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              className="bg-white/[0.06] border-white/[0.1] text-white placeholder:text-white/30 focus:border-secondary/50 focus:ring-secondary/20"
              {...register("password")}
            />
            {errors.password && (
              <p className="text-xs text-destructive" role="alert">
                {errors.password.message}
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-destructive text-center" role="alert">{error}</p>
          )}

          <Button
            type="submit"
            className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold h-11 shadow-glow"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t("login_submit_button")
            )}
          </Button>

          {/* Separator */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-xs text-white/40">{t("auth_or")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Google Sign-In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="w-full glass p-3 flex items-center justify-center gap-3 text-sm font-medium text-white hover:bg-white/[0.06] transition-colors rounded-md"
          >
            <svg viewBox="0 0 24 24" className="w-5 h-5">
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {t("auth_google")}
          </button>
        </form>

        <p
          className="text-center text-sm text-white/50 animate-fade-in-up"
          style={{ animationDelay: "200ms" }}
        >
          {t("login_no_account")}{" "}
          <Link
            to="/register"
            className="text-secondary font-semibold hover:text-secondary/80 transition-colors"
          >
            {t("login_sign_up")}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Login;
